import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useHttpUtils } from '../../Utilities/useHttpUtils';
import useAlert from '../../Alerts/useAlert';
import usePrivileges from '../../Auth/usePrivileges';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import ConfirmationDialog from '../../Dialogs/ConfirmationDialog';

interface BulkReprocessButtonProps {
    lotIds: Array<number>,
    hidden: boolean,
    setSelectedLots: Dispatch<SetStateAction<Array<number>>>,
    refreshLotList: () => void,
    affectedBusinessUnits : string[]
}

function BulkReprocessButton({ lotIds, affectedBusinessUnits, hidden = false, setSelectedLots, refreshLotList }: BulkReprocessButtonProps) {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { admin, businessUnitsUserCanProcess } = usePrivileges();

    const hasPermissions = admin ||
        (businessUnitsUserCanProcess !== undefined &&
        affectedBusinessUnits.every(affectedBusinessUnit =>
                businessUnitsUserCanProcess.some(
                    allowedBusinessUnit => allowedBusinessUnit === affectedBusinessUnit
                )
            ));

    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    const onClick = () => {
        setShowConfirmationDialog(true);
    };

    const bulkReprocess = async () => {
        fetchDataAuthenticated("api/v1/lots/bulkReprocess", "POST", JSON.stringify(lotIds))
            .then(async response => {
                setAlert("success", "Processing", `Bulk reprocessing lots...`);
                if (response.ok) {
                    let messageArray = JSON.parse(await response.text());
                    if (messageArray.length > 0) {
                        setAlert("danger", "Error", messageArray.join('\n'));
                    }
                    setSelectedLots([]);
                    refreshLotList();
                } else {
                    let message = await response.text();
                    throw new Error(`Failed to reprocess lots. ${message}`);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            });
    };

    return (
        <>
            <Button variant="secondary"
                hidden={hidden}
                disabled={!hasPermissions}
                onClick={onClick}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-1" />
                {t('LotList.ReprocessSelected')}
            </Button>
            <ConfirmationDialog showDialog={showConfirmationDialog} closeDialog={() => setShowConfirmationDialog(false)} onOk={bulkReprocess}>
                <p>Are you sure you want to reprocess the selected lots?</p>
            </ConfirmationDialog>
        </>
    );
}

export default BulkReprocessButton;